import React, { useEffect } from 'react';
import { usePostHogAnalytics, ANALYTICS_EVENTS } from '@/hooks/usePostHogAnalytics';

interface PerformanceMonitorProps {
  routeName?: string;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ routeName }) => {
  const { trackEvent } = usePostHogAnalytics();

  useEffect(() => {
    // Track Core Web Vitals
    const trackWebVitals = () => {
      if ('PerformanceObserver' in window) {
        // Largest Contentful Paint (LCP)
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
              feature: 'performance_monitoring',
              metric: 'lcp',
              value: entry.startTime,
              route: routeName || window.location.pathname
            });
          }
        }).observe({ entryTypes: ['largest-contentful-paint'] });

        // Cumulative Layout Shift (CLS)
        new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
            }
          }
          
          if (clsValue > 0) {
            trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
              feature: 'performance_monitoring',
              metric: 'cls',
              value: clsValue,
              route: routeName || window.location.pathname
            });
          }
        }).observe({ entryTypes: ['layout-shift'] });

        // First Input Delay (FID) via First Contentful Paint
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
              feature: 'performance_monitoring',
              metric: 'fcp',
              value: entry.startTime,
              route: routeName || window.location.pathname
            });
          }
        }).observe({ entryTypes: ['first-contentful-paint'] });
      }
    };

    // Track initial load performance
    const trackInitialLoad = () => {
      if (window.performance && window.performance.navigation) {
        const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
          feature: 'performance_monitoring',
          metric: 'page_load',
          load_time: navigation.loadEventEnd - navigation.loadEventStart,
          dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          route: routeName || window.location.pathname
        });
      }
    };

    // Debounce the tracking to avoid spam
    const timer = setTimeout(() => {
      trackWebVitals();
      trackInitialLoad();
    }, 1000);

    return () => clearTimeout(timer);
  }, [routeName, trackEvent]);

  // This component renders nothing but provides monitoring
  return null;
};

export default PerformanceMonitor;